import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { PROVINCES } from "@/data/provinces";
import { SECTORS } from "@/data/sectors";
import { INSIGHTS } from "@/data/insights";
import { SERVICES_FR } from "@/data/servicesFr";
import { TESTIMONIALS } from "@/data/testimonials";
import { REGIONS, findRegion } from "@/data/regions";

export const findService = (slug) => SERVICES.find((s) => s.slug === slug);
export const findProvince = (slug) => PROVINCES.find((p) => p.slug === slug);
export const findSector = (slug) => SECTORS.find((s) => s.slug === slug);
export const findInsight = (slug) => INSIGHTS.find((i) => i.slug === slug);
export const findCity = (pSlug, cSlug) => {
  const province = findProvince(pSlug);
  if (!province) return null;
  const city = province.cities.find((c) => c.slug === cSlug);
  return city ? { ...city, province } : null;
};

export const paths = {
  services: "/services",
  service: (s) => `/services/${s}`,
  locations: "/locations",
  province: (p) => `/locations/${p}`,
  city: (p, c) => `/locations/${p}/${c}`,
  serviceCity: (p, c, s) => `/locations/${p}/${c}/${s}`,
  sectors: "/sectors",
  sector: (s) => `/sectors/${s}`,
  insights: "/insights",
  insight: (s) => `/insights/${s}`,
  about: "/about",
};

export function getAllRoutes() {
  const routes = ["/", "/services", "/locations", "/sectors", "/insights", "/about"];
  SERVICES.forEach((s) => routes.push(paths.service(s.slug)));
  PROVINCES.forEach((p) => {
    routes.push(paths.province(p.slug));
    p.cities.forEach((c) => {
      routes.push(paths.city(p.slug, c.slug));
      SERVICES.forEach((s) => routes.push(paths.serviceCity(p.slug, c.slug, s.slug)));
    });
  });
  SECTORS.forEach((s) => routes.push(paths.sector(s.slug)));
  INSIGHTS.forEach((i) => routes.push(paths.insight(i.slug)));
  routes.push("/fr", "/fr/services");
  const qc = findProvince("quebec");
  SERVICES_FR.forEach((s) => routes.push(`/fr/services/${s.slug}`));
  qc.cities.forEach((c) => {
    routes.push(`/fr/villes/${c.slug}`);
    SERVICES_FR.forEach((s) => routes.push(`/fr/villes/${c.slug}/${s.slug}`));
  });
  routes.push("/regions");
  REGIONS.forEach((r) => routes.push(`/regions/${r.slug}`));
  return routes;
}

const localBusiness = (base) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: SITE.name,
  telephone: SITE.phoneIntl,
  email: SITE.email,
  url: base || undefined,
  address: {
    "@type": "PostalAddress",
    addressRegion: SITE.hq.code,
    addressCountry: "CA",
  },
  areaServed: { "@type": "Country", name: "Canada" },
  priceRange: "$$",
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "286" },
  review: TESTIMONIALS.slice(0, 3).map((t) => ({
    "@type": "Review",
    author: { "@type": "Person", name: t.name },
    reviewBody: t.text,
    reviewRating: { "@type": "Rating", ratingValue: "5" },
  })),
});

const breadcrumbLd = (base, items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${base}${it.path}`,
  })),
});

const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const NATIONAL_KEYWORDS =
  "cleaning services Canada, cleaning company Canada, professional cleaners, house cleaning services, commercial cleaning services, deep cleaning, maid service, janitorial services, carpet cleaning, window cleaning, pressure washing, move out cleaning, airbnb cleaning, post construction cleaning, eco friendly cleaning, cleaning services near me";

const meta = (title, description, keywords, jsonld) => ({
  title,
  description,
  keywords,
  jsonld: jsonld.filter(Boolean),
});

export function getMeta(path, base = "", extraFaqs = null) {
  const seg = path.split("/").filter(Boolean);
  const bc = (items) => breadcrumbLd(base, [{ name: "Home", path: "/" }, ...items]);

  if (seg.length === 0) {
    return meta(
      `Tbay Cleaning Services | Professional Cleaning Services Across Canada — House, Commercial & Deep Cleaning`,
      `Tbay Cleaning Services delivers insured, professional cleaning services in 13 provinces & territories across Canada. House cleaning, commercial cleaning, deep cleaning & more. Call ${SITE.phone}.`,
      NATIONAL_KEYWORDS,
      [
        localBusiness(base),
        { "@context": "https://schema.org", "@type": "WebSite", name: SITE.name, url: base },
      ]
    );
  }

  if (seg[0] === "services") {
    if (seg.length === 1) {
      return meta(
        `Cleaning Services in Canada | 12 Professional Services | Tbay Cleaning Services`,
        `Explore all 12 cleaning services from Tbay Cleaning Services: house cleaning, deep cleaning, commercial cleaning, carpet, window, move-in/out & more across Canada. Call ${SITE.phone}.`,
        `${NATIONAL_KEYWORDS}, cleaning services list, all cleaning services`,
        [bc([{ name: "Services", path: "/services" }])]
      );
    }
    const s = findService(seg[1]);
    if (!s) return notFoundMeta();
    return meta(
      `${s.name} Across Canada | Professional ${s.short} Services | Tbay Cleaning Services`,
      `${s.tagline} Insured, background-checked crews in 150+ Canadian cities. Free flat-rate quotes — call ${SITE.phone} or email ${SITE.email}.`,
      `${s.keywords.join(", ")}, ${s.short.toLowerCase()} Canada, ${s.short.toLowerCase()} near me`,
      [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: s.name,
          provider: { "@type": "LocalBusiness", name: SITE.name, telephone: SITE.phoneIntl, email: SITE.email },
          areaServed: { "@type": "Country", name: "Canada" },
        },
        bc([{ name: "Services", path: "/services" }, { name: s.name, path: path }]),
      ]
    );
  }

  if (seg[0] === "locations") {
    if (seg.length === 1) {
      return meta(
        `Cleaning Service Locations in Canada | 13 Provinces & Territories | Tbay Cleaning Services`,
        `Tbay Cleaning Services serves 150+ cities across all 13 Canadian provinces & territories. Find professional cleaning services near you. Call ${SITE.phone}.`,
        `${NATIONAL_KEYWORDS}, cleaning services by city, cleaning companies by province`,
        [bc([{ name: "Locations", path: "/locations" }])]
      );
    }
    const p = findProvince(seg[1]);
    if (!p) return notFoundMeta();
    if (seg.length === 2) {
      return meta(
        `Cleaning Services in ${p.name} | ${p.cities.length} Cities Served | Tbay Cleaning Services`,
        `Professional house cleaning, commercial cleaning, deep cleaning & 9 more services across ${p.name} — ${p.cities.map((c) => c.name).slice(0, 4).join(", ")} & more. Insured crews. Call ${SITE.phone}.`,
        `cleaning services ${p.name}, house cleaning ${p.name}, commercial cleaning ${p.name}, cleaners ${p.name}, maid service ${p.name}, janitorial services ${p.name}`,
        [bc([{ name: "Locations", path: "/locations" }, { name: p.name, path: path }])]
      );
    }
    const city = findCity(seg[1], seg[2]);
    if (!city) return notFoundMeta();
    if (seg.length === 3) {
      return meta(
        `Cleaning Services in ${city.name}, ${p.code} | 12 Services Available | Tbay Cleaning Services`,
        `Professional cleaning services in ${city.name}, ${p.name}: house cleaning, deep cleaning, commercial cleaning, move-in/out & more. Insured local crews. Call ${SITE.phone}.`,
        `cleaning services ${city.name}, cleaners ${city.name}, house cleaning ${city.name}, commercial cleaning ${city.name}, maid service ${city.name}, deep cleaning ${city.name}, ${city.name} cleaning company`,
        [
          localBusiness(base),
          bc([
            { name: "Locations", path: "/locations" },
            { name: p.name, path: paths.province(p.slug) },
            { name: city.name, path: path },
          ]),
        ]
      );
    }
    const s = findService(seg[3]);
    if (!s) return notFoundMeta();
    const faqs = extraFaqs || [];
    return meta(
      `${s.name} in ${city.name}, ${p.code} | Top-Rated ${s.short} | Tbay Cleaning Services`,
      `Professional ${s.short.toLowerCase()} in ${city.name}, ${p.name}. ${s.tagline} Fully insured crews, flat-rate quotes. Call ${SITE.phone}.`,
      `${s.keywords.map((k) => `${k} ${city.name}`).join(", ")}, ${s.short.toLowerCase()} in ${city.name}, best ${s.short.toLowerCase()} ${city.name}, ${city.name} ${s.short.toLowerCase()} company, affordable ${s.short.toLowerCase()} ${city.name} ${p.code}`,
      [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: s.name,
          provider: { "@type": "LocalBusiness", name: SITE.name, telephone: SITE.phoneIntl, email: SITE.email },
          areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "AdministrativeArea", name: p.name } },
        },
        bc([
          { name: "Locations", path: "/locations" },
          { name: p.name, path: paths.province(p.slug) },
          { name: city.name, path: paths.city(p.slug, city.slug) },
          { name: s.name, path: path },
        ]),
        faqs.length ? faqLd(faqs) : null,
      ]
    );
  }

  if (seg[0] === "sectors") {
    if (seg.length === 1) {
      return meta(
        `Sectors We Serve | Commercial Cleaning by Industry | Tbay Cleaning Services`,
        `Specialized cleaning programs for offices, healthcare, retail, education, industrial, hospitality & more across Canada. Call ${SITE.phone}.`,
        "commercial cleaning by industry, office building cleaning, healthcare facility cleaning, retail cleaning, industrial cleaning Canada",
        [bc([{ name: "Sectors", path: "/sectors" }])]
      );
    }
    const s = findSector(seg[1]);
    if (!s) return notFoundMeta();
    return meta(
      `${s.name} Cleaning Services in Canada | Tbay Cleaning Services`,
      `${s.desc} Specialized ${s.name.toLowerCase()} cleaning programs nationwide. Insured, supervised, quality-audited. Call ${SITE.phone}.`,
      `${s.name.toLowerCase()} cleaning, ${s.name.toLowerCase()} cleaning services Canada, ${s.name.toLowerCase()} janitorial`,
      [bc([{ name: "Sectors", path: "/sectors" }, { name: s.name, path: path }])]
    );
  }

  if (seg[0] === "insights") {
    if (seg.length === 1) {
      return meta(
        `Cleaning Insights & Guides | Tips, Costs & Checklists | Tbay Cleaning Services`,
        `Expert Canadian cleaning guides: costs, checklists, hiring tips, and industry advice from Tbay Cleaning Services.`,
        "cleaning tips Canada, cleaning cost guide, cleaning checklists, cleaning advice",
        [bc([{ name: "Insights", path: "/insights" }])]
      );
    }
    const i = findInsight(seg[1]);
    if (!i) return notFoundMeta();
    return meta(
      `${i.title} | Tbay Cleaning Services`,
      i.excerpt,
      `${i.category.toLowerCase()}, cleaning services Canada, ${i.title.toLowerCase()}`,
      [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: i.title,
          datePublished: i.date,
          author: { "@type": "Organization", name: SITE.name },
          publisher: { "@type": "Organization", name: SITE.name },
        },
        bc([{ name: "Insights", path: "/insights" }, { name: i.title, path: path }]),
      ]
    );
  }

  if (seg[0] === "regions") {
    if (seg.length === 1) {
      return meta(
        `Regional Cleaning Service Areas in Canada | Regional Hubs | Tbay Cleaning Services`,
        `Explore regional cleaning service hubs across Canada — Northern Manitoba, Metro Vancouver, the GTHA & more. Insured local crews, one national number: ${SITE.phone}.`,
        "regional cleaning services canada, northern manitoba cleaning services, metro vancouver cleaning, gtha cleaning services, regional cleaning company canada, cleaning service areas",
        [bc([{ name: "Locations", path: "/locations" }, { name: "Regions", path: "/regions" }])]
      );
    }
    const region = findRegion(seg[1]);
    if (!region) return notFoundMeta();
    const rp = findProvince(region.province);
    const rc = region.cities
      .map((s) => { const c = rp && rp.cities.find((x) => x.slug === s); return c ? c.name : null; })
      .filter(Boolean);
    return meta(
      `Cleaning Services in ${region.name} | ${rc.length} Communities Served | Tbay Cleaning Services`,
      `Professional cleaning across ${region.name}: ${rc.slice(0, 4).join(", ")}${rc.length > 4 ? " & more" : ""}. House, commercial & specialty cleaning by insured local crews. Call ${SITE.phone}.`,
      `cleaning services ${region.name.toLowerCase()}, cleaners in ${region.name.toLowerCase()}, house cleaning ${region.name.toLowerCase()}, commercial cleaning ${region.name.toLowerCase()}, cleaning company ${region.name.toLowerCase()}${rp ? ` ${rp.code.toLowerCase()}` : ""}`,
      [
        bc([{ name: "Locations", path: "/locations" }, { name: "Regions", path: "/regions" }, { name: region.name, path: path }]),
      ]
    );
  }

  if (seg[0] === "fr") {
    const qc = findProvince("quebec");
    const frBc = (items) => breadcrumbLd(base, [{ name: "Accueil", path: "/fr" }, ...items]);
    if (seg.length === 1) {
      return meta(
        `Tbay Cleaning Services | Services de nettoyage professionnels au Québec — Résidentiel et commercial`,
        `Nettoyage résidentiel et commercial partout au Québec : entretien ménager, grand nettoyage, conciergerie, nettoyage Airbnb et plus. Équipes assurées et vérifiées. Appelez le ${SITE.phone}.`,
        "services de nettoyage québec, entretien ménager québec, nettoyage commercial québec, femme de ménage québec, grand ménage, nettoyage après construction québec, lavage de vitres, lavage sous pression québec, entreprise de nettoyage",
        [frBc([])]
      );
    }
    if (seg[1] === "services") {
      if (seg.length === 2) {
        return meta(
          `Services de nettoyage au Québec | 12 services professionnels | Tbay Cleaning Services`,
          `Découvrez les 12 services Tbay Cleaning Services au Québec : nettoyage résidentiel, grand nettoyage, commercial, tapis, vitres, déménagement et plus. Appelez le ${SITE.phone}.`,
          "services de nettoyage québec, liste services nettoyage, entretien ménager, conciergerie québec",
          [frBc([{ name: "Services", path: "/fr/services" }])]
        );
      }
      const s = SERVICES_FR.find((x) => x.slug === seg[2]);
      if (!s) return notFoundMeta();
      return meta(
        `${s.name} au Québec | Tbay Cleaning Services`,
        `${s.tagline} Équipes assurées et vérifiées dans ${qc.cities.length} villes du Québec. Soumissions à prix fixe — appelez le ${SITE.phone}.`,
        `${s.short.toLowerCase()} québec, ${s.short.toLowerCase()} montréal, entreprise de ${s.short.toLowerCase()}, prix ${s.short.toLowerCase()} québec`,
        [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: s.name,
            provider: { "@type": "LocalBusiness", name: SITE.name, telephone: SITE.phoneIntl, email: SITE.email },
            areaServed: { "@type": "AdministrativeArea", name: "Québec" },
          },
          frBc([{ name: "Services", path: "/fr/services" }, { name: s.name, path: path }]),
        ]
      );
    }
    if (seg[1] === "villes") {
      const city = findCity("quebec", seg[2]);
      if (!city) return notFoundMeta();
      if (seg.length === 3) {
        return meta(
          `Services de nettoyage à ${city.name}, QC | 12 services | Tbay Cleaning Services`,
          `Nettoyage professionnel à ${city.name}, Québec : entretien ménager, grand nettoyage, commercial, déménagement et plus. Équipes locales assurées. Appelez le ${SITE.phone}.`,
          `nettoyage ${city.name.toLowerCase()}, services de nettoyage ${city.name.toLowerCase()}, entretien ménager ${city.name.toLowerCase()}, entreprise de nettoyage ${city.name.toLowerCase()}, femme de ménage ${city.name.toLowerCase()}`,
          [
            localBusiness(base),
            frBc([{ name: "Villes", path: "/fr#villes" }, { name: city.name, path: path }]),
          ]
        );
      }
      const s = SERVICES_FR.find((x) => x.slug === seg[3]);
      if (!s) return notFoundMeta();
      const faqs = extraFaqs || [];
      return meta(
        `${s.name} à ${city.name}, QC | Tbay Cleaning Services`,
        `${s.name} professionnel à ${city.name}, Québec. ${s.tagline} Équipes assurées, prix fixes. Appelez le ${SITE.phone}.`,
        `${s.short.toLowerCase()} ${city.name.toLowerCase()}, ${s.short.toLowerCase()} à ${city.name.toLowerCase()}, meilleure entreprise de nettoyage ${city.name.toLowerCase()}, prix nettoyage ${city.name.toLowerCase()}`,
        [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: s.name,
            provider: { "@type": "LocalBusiness", name: SITE.name, telephone: SITE.phoneIntl, email: SITE.email },
            areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "AdministrativeArea", name: "Québec" } },
          },
          frBc([{ name: "Villes", path: "/fr#villes" }, { name: city.name, path: `/fr/villes/${city.slug}` }, { name: s.name, path: path }]),
          faqs.length ? faqLd(faqs) : null,
        ]
      );
    }
    return notFoundMeta();
  }

  if (seg[0] === "about") {
    return meta(
      `About Tbay Cleaning Services | Canadian Cleaning Company, Coast to Coast`,
      `Proudly Canadian from day one: Tbay Cleaning Services is a national cleaning network serving 13 provinces & territories with insured, vetted local crews.`,
      "about Tbay Cleaning Services, Canadian cleaning company, national cleaning company Canada, national cleaning services Canada",
      [localBusiness(base), bc([{ name: "About", path: "/about" }])]
    );
  }

  return notFoundMeta();
}

const notFoundMeta = () => meta(
  `Page Not Found | Tbay Cleaning Services`,
  `The page you're looking for has moved. Explore Tbay Cleaning Services services and locations across Canada.`,
  "cleaning services Canada",
  []
);
